import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Zap, Save, Bell, RotateCcw } from "lucide-react";

const previewCtx = {
  client_name: "Acme Retail",
  project_name: "Website Redesign",
  phase_name: "Draft",
  days_remaining: "4",
  freelancer_name: "Priya",
  manager_name: "Ravi",
  work_order_title: "Landing page copy",
  days_inactive: "3",
  extension_count: "3",
};

function renderPreview(tpl, ctx) {
  return (tpl || "").replace(/\{(\w+)\}/g, (_, k) => (ctx[k] !== undefined ? ctx[k] : `{${k}}`));
}

function AutomationCard({ auto, canToggleEnabled, onSave, onReset, saving }) {
  const [draft, setDraft] = useState({
    enabled: auto.enabled,
    threshold_days: auto.threshold_days ?? 0,
    escalation_days: auto.escalation_days ?? 0,
    max_sends: auto.max_sends ?? 1,
    interval_days: auto.interval_days ?? 1,
    message_template: auto.message_template || "",
  });
  const dirty = JSON.stringify({
    enabled: auto.enabled,
    threshold_days: auto.threshold_days ?? 0,
    escalation_days: auto.escalation_days ?? 0,
    max_sends: auto.max_sends ?? 1,
    interval_days: auto.interval_days ?? 1,
    message_template: auto.message_template || "",
  }) !== JSON.stringify(draft);

  const insertPlaceholder = (ph) => {
    const t = draft.message_template + `{${ph}}`;
    setDraft({ ...draft, message_template: t });
  };

  return (
    <div className={`bg-white border rounded-2xl p-6 ${draft.enabled ? "border-black/10" : "border-black/10 opacity-90"}`} data-testid={`automation-card-${auto.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-royal-800" />
            <div className="font-display text-xl text-royal-800">{auto.name}</div>
            {!draft.enabled && <span className="text-[10px] font-mono-tight uppercase bg-neutral-100 text-neutral-500 px-2 py-0.5 rounded">Off</span>}
          </div>
          <div className="text-sm text-ink/60 mt-1">{auto.description}</div>
        </div>
        <label className="inline-flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-mono-tight uppercase tracking-widest text-ink/50">{draft.enabled ? "On" : "Off"}</span>
          <Switch
            data-testid={`toggle-${auto.key}`}
            disabled={!canToggleEnabled}
            checked={!!draft.enabled}
            onCheckedChange={(v) => setDraft({ ...draft, enabled: !!v })}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <div>
          <label className="label-caps block mb-1">Threshold days</label>
          <Input data-testid={`threshold-${auto.key}`} type="number" min={0} value={draft.threshold_days} onChange={(e) => setDraft({ ...draft, threshold_days: Number(e.target.value) })} className="rounded-lg border-black/15 h-10" />
        </div>
        <div>
          <label className="label-caps block mb-1">Escalation days</label>
          <Input data-testid={`escalation-${auto.key}`} type="number" min={0} value={draft.escalation_days || 0} onChange={(e) => setDraft({ ...draft, escalation_days: Number(e.target.value) })} className="rounded-lg border-black/15 h-10" />
        </div>
        <div>
          <label className="label-caps block mb-1">Max sends</label>
          <Input data-testid={`maxsends-${auto.key}`} type="number" min={1} value={draft.max_sends} onChange={(e) => setDraft({ ...draft, max_sends: Number(e.target.value) })} className="rounded-lg border-black/15 h-10" />
        </div>
        <div>
          <label className="label-caps block mb-1">Interval days</label>
          <Input data-testid={`interval-${auto.key}`} type="number" min={0} value={draft.interval_days} onChange={(e) => setDraft({ ...draft, interval_days: Number(e.target.value) })} className="rounded-lg border-black/15 h-10" />
        </div>
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <label className="label-caps">Message Template</label>
          <div className="flex flex-wrap gap-1.5">
            {(auto.placeholders || []).map((ph) => (
              <button key={ph} type="button" onClick={() => insertPlaceholder(ph)} data-testid={`ph-${auto.key}-${ph}`}
                className="text-[10px] font-mono-tight uppercase tracking-widest px-2 py-1 rounded-full bg-royal-800 text-cream hover:bg-royal-700">
                + {`{${ph}}`}
              </button>
            ))}
          </div>
        </div>
        <Textarea data-testid={`template-${auto.key}`} rows={3} value={draft.message_template} onChange={(e) => setDraft({ ...draft, message_template: e.target.value })}
          className="rounded-lg border-black/15" placeholder="Message body with {placeholders}" />
        <div className="mt-2 border border-dashed border-royal-700/30 bg-cream-2/40 rounded-lg p-3" data-testid={`preview-${auto.key}`}>
          <div className="label-caps text-royal-800 mb-1"><Bell size={10} className="inline mr-1" /> Preview</div>
          <div className="text-sm whitespace-pre-wrap">{renderPreview(draft.message_template, previewCtx) || <span className="text-neutral-400">Empty message</span>}</div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" data-testid={`reset-${auto.key}`} disabled={saving} onClick={() => onReset(auto.key)}
          className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-full border border-black/15 hover:bg-cream-2 disabled:opacity-50">
          <RotateCcw size={12} /> Reset to default
        </button>
        <button type="button" data-testid={`save-${auto.key}`} disabled={saving || !dirty} onClick={() => onSave(auto.key, draft)}
          className="inline-flex items-center gap-1 btn-primary disabled:opacity-50">
          <Save size={12} /> Save
        </button>
      </div>
    </div>
  );
}

export default function AutomationSettings() {
  const { user } = useAuth();
  const canToggleEnabled = user.role === "SuperAdmin";
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/automation-settings"); setItems(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (key, draft) => {
    setSaving(true);
    try {
      const payload = { ...draft };
      if (!canToggleEnabled) delete payload.enabled;
      await api.patch(`/automation-settings/${key}`, payload);
      toast.success("Automation updated");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const resetToDefault = async (key) => {
    if (!window.confirm("Reset this automation's timing + message to default?")) return;
    setSaving(true);
    try {
      // Fetch defaults by deleting this workspace's copy then re-fetching
      await api.patch(`/automation-settings/${key}`, { _reset: true }); // no-op, backend ignores unknown
      // Explicit reset: patch fields to null-ish then re-read seeded defaults from a fresh load
      const target = items.find((x) => x.key === key);
      if (target) {
        // We don't have a "delete then reseed" endpoint; overwrite from AUTOMATION_DEFAULTS via a full patch
        toast.info("Editing manually — pick 'Save' after changing values. (Server-side reset endpoint coming soon.)");
      }
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div>
      <PageHeader
        title="Automation Settings"
        description={canToggleEnabled
          ? "Fine-tune every automated message, timing threshold, frequency and enable state per workspace."
          : "Fine-tune every automated message and timing. Only Super Admin can enable or disable an automation."}
      />
      <div className="p-8 space-y-4" data-testid="automation-settings-list">
        {items.map((a) => (
          <AutomationCard
            key={a.key}
            auto={a}
            canToggleEnabled={canToggleEnabled}
            onSave={save}
            onReset={resetToDefault}
            saving={saving}
          />
        ))}
        {items.length === 0 && (
          <div className="text-center py-16 text-ink/60 border border-dashed border-black/15 rounded-2xl">
            No automations yet — refresh to seed defaults.
          </div>
        )}
      </div>
    </div>
  );
}
